import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Footer } from './Footer';

const processFaqs = [
  {
    question: "What is dynamic pricing in manufacturing?",
    answer: "Dynamic pricing in manufacturing automatically adjusts product prices based on real-time production costs, raw material prices, and market demand. This keeps profit margins consistent even when input costs change — protecting profitability without manual repricing delays."
  },
  {
    question: "What industries benefit from process cost control?",
    answer: "Food, beverage, and tobacco; pharmaceutical; agriculture; and wood and paper products all benefit directly. Any production environment with variable input costs, quality compliance requirements, or volatile raw material prices gains measurable value from real-time process monitoring."
  },
  {
    question: "How does production monitoring reduce waste?",
    answer: "By measuring flow rates, quantities, and resource consumption in real time, process monitoring identifies waste, process deviations, and equipment inefficiencies as they occur — not after the production run. Early detection typically reduces waste by 15–30% in the first year."
  },
  {
    question: "Can process monitoring integrate with existing ERP systems?",
    answer: "Yes. Yuccan Tech's process monitoring platform supports integration with procurement and ERP systems. Raw material consumption, energy usage, and production data link directly to your existing business intelligence and reporting workflows."
  }
];

const processFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": processFaqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
  }))
};

const processBreadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://yuccan.tech/" },
    { "@type": "ListItem", "position": 2, "name": "Process Cost Control & Dynamic Pricing", "item": "https://yuccan.tech/dynamic-pricing" }
  ]
};

const ProcessPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    fleetSize: '',
    message: ''
  });

  const [transactionalConsent, setTransactionalConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const [showPhoneHint, setShowPhoneHint] = useState(false);

  // Load reCAPTCHA script on component mount
  useEffect(() => {
    // Skip reCAPTCHA injection during react-snap / headless pre-rendering
    if (navigator.webdriver || window.location.hostname === 'localhost') return;
    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js?render=6Ld5QC0tAAAAACBa4DGyEe6SVLaGCVB8huTeuxFF";
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      window.grecaptcha.ready(() => {
        console.log('reCAPTCHA v3 loaded successfully');
      });
    };
    
    script.onerror = () => {
      console.error('Failed to load reCAPTCHA script');
    };
    
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const filtered = value.replace(/[^+\d]/g, '');
      const sanitized = filtered.charAt(0) === '+'
        ? '+' + filtered.slice(1).replace(/\+/g, '')
        : filtered.replace(/\+/g, '');
      if (sanitized.length > 0 && sanitized.charAt(0) !== '+') {
        setShowPhoneHint(true);
        setTimeout(() => setShowPhoneHint(false), 2500);
        return;
      }
      if (sanitized !== value) {
        setShowPhoneHint(true);
        setTimeout(() => setShowPhoneHint(false), 2500);
      }
      setFormData(prev => ({ ...prev, phone: sanitized }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Execute reCAPTCHA v3 if available
      let recaptchaToken = null;
      if (window.grecaptcha) {
        recaptchaToken = await new Promise((resolve) => {
          window.grecaptcha.ready(async () => {
            try {
              const token = await window.grecaptcha.execute('6Ld5QC0tAAAAACBa4DGyEe6SVLaGCVB8huTeuxFF', {action: 'submit'});
              resolve(token);
            } catch (error) {
              resolve(null);
            }
          });
        });
      }

      // Prepare form data for API (match main contact form structure)
      const apiFormData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone.trim(),
        transactionalConsent: formData.phone.trim() ? transactionalConsent : false,
        marketingConsent: formData.phone.trim() ? marketingConsent : false,
        message: `Company: ${formData.company}\nAverage Production Output: ${formData.fleetSize}\n\nMessage: ${formData.message}`,
        formStartTime: Date.now() - 10000, // Anti-spam timing
        honeypot: '', // Anti-spam field
        recaptchaToken // Add reCAPTCHA token
      };

      // Make API call to backend
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiFormData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setSubmitMessage('Thank you! We\'ll contact you within 24 hours to discuss your dynamic pricing needs.');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          fleetSize: '',
          message: ''
        });
        setTransactionalConsent(false);
        setMarketingConsent(false);
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('Sorry, there was an error sending your message. Please try again or contact us directly on +64 9 886 4638.');
    }
    
    setIsSubmitting(false);
  };

  return (
    <div>
      <Helmet>
        <title>Process Cost Control & Dynamic Pricing | Yuccan Tech</title>
        <meta name="description" content="Real-time production monitoring, market-linked dynamic pricing, and profit maximisation for food, pharma, agriculture & manufacturing businesses." />
        <link rel="canonical" href="https://yuccan.tech/dynamic-pricing" />
        <meta property="og:title" content="Process Cost Control & Dynamic Pricing | Yuccan Tech" />
        <meta property="og:description" content="Real-time process monitoring and intelligent dynamic pricing for food, pharma, agriculture, and manufacturing. Maximise margins with live data." />
        <meta property="og:url" content="https://yuccan.tech/dynamic-pricing" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(processFaqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(processBreadcrumb)}</script>
      </Helmet>
    <div className="process-page">
      {/* Logo Header */}
      <div className="telematics-header">
        <div className="container">
          <div className="logo-container">
            <a href="/">
              <img 
                src="/img/Banner_16x_TR_Trim&ColorEdit.png" 
                alt="Yuccan Technologies Logo" 
                className="telematics-logo"
              />
            </a>
          </div>
        </div>
      </div>
      {/* Hero Section */}
      <section className="process-hero">
        <video 
          autoPlay
          muted
          loop
          playsInline
          onPlay={() => {
            // Release wake lock to allow screen sleep
            if ('wakeLock' in navigator) {
              navigator.wakeLock.request('screen').then(wakeLock => {
                wakeLock.release();
              }).catch(() => {});
            }
          }}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            top: 0,
            left: 0,
            zIndex: -1
          }}
        >
          <source src="/vid/Process.mp4" type="video/mp4" />
        </video>
        
        <div className="hero-overlay">
          <div className="container">
            <div className="row">
              <div className="col-md-6">
                <div className="hero-content-overlay">
                  <div className="hero-two-column">
                    <div className="hero-left-column">
                      <h1 className="hero-title" style={{marginBottom: '10px', marginTop: '0px'}}>
                        Process Cost Control.<br />
                        <span className="text-primary">Market Conditions Visibility.</span>
                      </h1>
                      <p className="hero-subtitle" style={{marginTop: '0px', marginBottom: '0px'}}>
                        Monitor and control your process inputs in real-time, track batch production outputs for superior QC/QA, and use predictive maintenance strategies to reduce equipment and component failure.   
                        Be proactive rather than reactive with real-time web tools and services that keep you aware of what's happening in your markets and provide an edge in unit pricing decisions. Combine these two key concepts to reduce expenses and provide optimal product pricing for profit maximization.
                      </p>
                    </div>
                    <div className="hero-right-column">
                      <div className="hero-stats">
                        <div className="stat-item">
                          <div className="stat-number">100%</div>
                          <div className="stat-label">Process Control</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-number">24/7</div>
                          <div className="stat-label">Real-time Monitoring</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-number">Full</div>
                          <div className="stat-label">Pricing Decision Visibility</div>
                        </div>
                      </div>
                      <div className="hero-cta">
                        <a href="#features" className="btn btn-outline-primary btn-lg">View Core Features</a>
                        <a href="#contact" className="btn btn-primary btn-lg">Get Dynamic Pricing Capability</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile-only Hero Content Section */}
      <section className="mobile-hero-content">
        <div className="container">
          <div className="row">
            <div className="col-xs-12">
              <div className="mobile-hero-content-container">
                <h1 className="mobile-hero-title">
                  Process Cost Control.<br />
                  <span className="text-primary">Market Conditions Visibility.</span>
                </h1>
                <p className="mobile-hero-subtitle">
                  Monitor and control your process inputs in real-time, track batch production outputs for superior QC/QA, and use predictive maintenance strategies to reduce equipment and component failure.   
                  Be proactive rather than reactive with real-time web tools and services that keep you aware of what's happening in your markets and provide an edge in unit pricing decisions. Combine these two key concepts to reduce expenses and provide optimal product pricing for profit maximization.
                </p>
                <div className="mobile-hero-stats">
                  <div className="mobile-stat-item">
                    <div className="mobile-stat-number">100%</div>
                    <div className="mobile-stat-label">Process Control</div>
                  </div>
                  <div className="mobile-stat-item">
                    <div className="mobile-stat-number">24/7</div>
                    <div className="mobile-stat-label">Real-time Monitoring</div>
                  </div>
                  <div className="mobile-stat-item">
                    <div className="mobile-stat-number">Full</div>
                    <div className="mobile-stat-label">Pricing Decision Visibility</div>
                  </div>
                </div>
                <div className="mobile-hero-cta">
                  <a href="#features" className="btn btn-outline-primary btn-lg">View Core Features</a>
                  <a href="#contact" className="btn btn-primary btn-lg">Get Dynamic Pricing Capability</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section id="benefits" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Profit Margin Maximization</h2>
            <p>Achieve high-level process efficiency and concurrently keep prices competitive; use modern technology to implement profit maximization</p>
          </div>
          <div className="row">
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-cogs"></i>
                <div className="service-desc">
                  <h3>Process Monitoring & Control</h3>
                  <p>Real-time monitoring of your production quantities with advanced sensors. Track flow rates and quantity parameters to maintain optimal production costs. Automated alerts prevent process deviations and reduce waste while maximizing throughput efficiency.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-line-chart"></i>
                <div className="service-desc">
                  <h3>Market Conditions Monitoring</h3>
                  <p>Continuous monitoring of raw material prices, supply chain fluctuations, competitor pricing, and market demand patterns. Real-time data feeds provide you with the intelligence needed to make informed pricing decisions and maintain competitive advantage in volatile markets.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-tags"></i>
                <div className="service-desc">
                  <h3>Dynamic Price Control</h3>
                  <p>Intelligent pricing algorithms that automatically adjust product prices based on production costs, market conditions, demand forecasting, and competitor analysis. Maximize profit margins while maintaining market competitiveness through real-time price optimization.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section id="features" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Core Features</h2>
            <p>Everything you need to manage your manufacturing processes effectively in one integrated platform</p>
          </div>
          <div className="row">
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-bar-chart"></i>
                <div className="service-desc">
                  <h3>Real-Time Process Analytics</h3>
                  <p>Live monitoring of production metrics. Track efficiency ratios, quality indicators, and resource consumption with comprehensive historical analysis for complete process visibility and optimization opportunities.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-tachometer-alt"></i>
                <div className="service-desc">
                  <h3>Equipment Performance Monitoring</h3>
                  <p>Monitor equipment vibration, temperature, pressure, and operational efficiency. Use automated performance scorecards and maintenance alerts to improve production reliability and prevent costly breakdowns.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-calculator"></i>
                <div className="service-desc">
                  <h3>Resource Cost Management</h3>
                  <p>Monitor raw material consumption, energy usage, and labor costs in real-time. Detect waste patterns, optimize resource allocation, and maximize efficiency. Integration with procurement and ERP systems available.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-wrench"></i>
                <div className="service-desc">
                  <h3>Predictive Maintenance</h3>
                  <p>Maintenance scheduling based on equipment usage, sensor data, and performance trends. Reduce unplanned downtime and extend equipment life through intelligent maintenance planning and early failure detection.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-mobile"></i>
                <div className="service-desc">
                  <h3>Mobile Operator Interface</h3>
                  <p>Native iOS and Android apps for production operators with shift logging, quality control forms, maintenance requests, and real-time performance dashboards; accessible from anywhere on the factory floor and beyond.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-chart-pie"></i>
                <div className="service-desc">
                  <h3>Advanced Analytics & Reporting</h3>
                  <p>Comprehensive production dashboards with customizable KPIs and reports. Export data for financial analysis, regulatory compliance, and strategic business intelligence to optimize profitability and operational efficiency.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Applications Section */}
      <section id="industries" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Necessary for Modern Production</h2>
            <p>Our process monitoring and dynamic pricing solutions deliver results for businesses of all sizes and types</p>
          </div>
          <div className="row">
            <div className="col-md-3 col-sm-6 col-xs-12">
              <div className="industry-card">
                <div className="industry-photo industry-fb&t" style={{backgroundImage: 'url(/img/dynamic-pricing/industry-fb&t.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Food, Beverage & Tobacco</h3>
                  <p>On-the-fly QC/QA, real-time food recipe monitoring, live process feedback.</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 col-xs-12">
              <div className="industry-card">
                <div className="industry-photo industry-pharma" style={{backgroundImage: 'url(/img/dynamic-pricing/industry-pharma.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Pharmaceuticals</h3>
                  <p>Precision API monitoring, real-time compliance monitoring, batch cost-control via market feedback, compliance documentation.</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 col-xs-12">
              <div className="industry-card">
                <div className="industry-photo industry-agri" style={{backgroundImage: 'url(/img/dynamic-pricing/industry-agri.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Agribusiness</h3>
                  <p>Dairy production quantity monitoring, livestock feed quantity control, market-driven resource allocation.</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 col-xs-12">
              <div className="industry-card">
                <div className="industry-photo industry-w&pp" style={{backgroundImage: 'url(/img/dynamic-pricing/industry-w&pp.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Wood & Paper Products</h3>
                  <p>Real-time raw material cost monitoring, process input quantity monitoring, production scheduling optimization.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Showcase */}
      <section id="technology" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Cutting-Edge Technology Stack</h2>
            <p>Enterprise-grade hardware and software designed for turnkey global use</p>
          </div>
          <div className="row">
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="technology-card">
                <div className="technology-photo technology-dashboard" style={{backgroundImage: 'url(/img/dynamic-pricing/technology-dashboard.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Intuitive Dashboard</h3>
                  <p>Clean, responsive interface providing instant access to critical process information. Customizable widgets and real-time data visualization.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="technology-card">
                <div className="technology-photo technology-mobile" style={{backgroundImage: 'url(/img/dynamic-pricing/technology-mobile.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Mobile Applications</h3>
                  <p>Native iOS and Android apps for operators and plant floor managers. Seamless synchronization with edge data stack.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="technology-card">
                <div className="technology-photo technology-hardware" style={{backgroundImage: 'url(/img/dynamic-pricing/technology-hardware.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Robust Hardware</h3>
                  <p>Professional-grade wired (RS485, Ethernet) & wireless (Wifi, Bluetooth, LoRaWAN & cellular), web-interface enabled devices.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="text-center" style={{paddingTop: '60px', paddingBottom: '60px', backgroundColor: '#f8f9fa'}}>
        <div className="container">
          <div className="section-title">
            <h2>Process Monitoring & Dynamic Pricing — FAQ</h2>
          </div>
          <div className="row">
            <div className="col-md-8 col-md-offset-2" style={{textAlign: 'left'}}>
              {processFaqs.map((faq, i) => (
                <div key={i} style={{borderBottom: '1px solid #dee2e6', marginBottom: '0'}}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      width: '100%', padding: '18px 0', background: 'none', border: 'none',
                      textAlign: 'left', fontSize: '16px', fontWeight: '600', color: '#333', cursor: 'pointer'
                    }}
                  >
                    <span>{faq.question}</span>
                    <span style={{fontSize: '20px', color: '#f8825a', marginLeft: '16px', flexShrink: 0}}>
                      {openFaq === i ? '−' : '+'}
                    </span>
                  </button>
                  {openFaq === i && (
                    <div style={{paddingBottom: '18px', color: '#555', lineHeight: '1.7', fontSize: '15px'}}>
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact/Get Started Section */}
      <section id="contact" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Ready to Maximize Your Profits?</h2>
            <p>Get started with a free no-obligation plant assessment and discover how much you could save with our dynamic pricing solution.</p>
          </div>
          <div className="row">
            <div className="col-md-8 col-md-offset-2">
                <form onSubmit={handleSubmit} className="process-contact-form">
                  <div className="form-group">
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      placeholder="Your Name *"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          placeholder="Email Address *"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <input
                          type="text"
                          name="company"
                          className="form-control"
                          placeholder="Company Name"
                          value={formData.company}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group" style={{position: 'relative'}}>
                        <input
                          type="tel"
                          name="phone"
                          className="form-control"
                          placeholder="Phone Number"
                          value={formData.phone}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        />
                        {showPhoneHint && (
                          <div style={{
                            position: 'absolute', top: '-38px', left: '0', right: '0',
                            backgroundColor: '#333', color: '#fff', fontSize: '12px',
                            padding: '6px 10px', borderRadius: '4px', textAlign: 'center',
                            zIndex: 10, whiteSpace: 'nowrap'
                          }}>
                            Only + followed by digits allowed
                            <div style={{
                              position: 'absolute', bottom: '-6px', left: '50%', marginLeft: '-6px',
                              width: 0, height: 0, borderLeft: '6px solid transparent',
                              borderRight: '6px solid transparent', borderTop: '6px solid #333'
                            }} />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <select
                          name="fleetSize"
                          className="form-control"
                          value={formData.fleetSize}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        >
                          <option value="">Select Average Production Output</option>
                          <option value="1-5">1-5 units/hour</option>
                          <option value="6-20">6-20 units/hour</option>
                          <option value="21-50">21-50 units/hour</option>
                          <option value="51-100">51-100 units/hour</option>
                          <option value="100+">100+ units/hour</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <textarea
                      name="message"
                      className="form-control"
                      rows="4"
                      placeholder="Tell us about your production challenges or goals..."
                      value={formData.message}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                    ></textarea>
                  </div>

                  {formData.phone.trim() && (
                    <>
                      <div style={{clear: 'both', overflow: 'hidden', marginTop: '-5px', marginBottom: '10px'}}>
                        <label style={{display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '13px', color: '#fff', fontWeight: 'normal', float: 'none', width: '100%'}}>
                          <input
                            type="checkbox"
                            checked={transactionalConsent}
                            onChange={(e) => setTransactionalConsent(e.target.checked)}
                            disabled={isSubmitting}
                            style={{marginRight: '8px', marginTop: '2px', flexShrink: 0}}
                          />
                          <span style={{textAlign: 'justify'}}>
                            By checking this box, I consent to receive non-marketing text messages from Yuccan Technologies at the phone number provided. Messages may include responses to inquiries, appointment or consultation confirmations, service updates, and follow-up communications relevant to your request. Message frequency is twice or less per day; message and data rates may apply. Reply STOP to opt out.
                          </span>
                        </label>
                      </div>
                      <div style={{clear: 'both', overflow: 'hidden', marginBottom: '15px'}}>
                        <label style={{display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '13px', color: '#fff', fontWeight: 'normal', float: 'none', width: '100%'}}>
                          <input
                            type="checkbox"
                            checked={marketingConsent}
                            onChange={(e) => setMarketingConsent(e.target.checked)}
                            disabled={isSubmitting}
                            style={{marginRight: '8px', marginTop: '2px', flexShrink: 0}}
                          />
                          <span style={{textAlign: 'justify'}}>
                            By checking this box, I consent to receive marketing and promotional text messages from Yuccan Technologies at the phone number provided. Messages may include special offers, discounts, and new product updates among others. Message frequency is twice or less per day; message and data rates may apply. Reply STOP to opt out.
                          </span>
                        </label>
                      </div>
                    </>
                  )}

                  {submitMessage && (
                    <div className={`alert ${submitStatus === 'success' ? 'alert-success' : 'alert-danger'}`}>
                      {submitMessage}
                    </div>
                  )}

                  <div className="privacy-notice" style={{margin: '15px 0', fontSize: '14px', color: '#fff'}}>
                    <p style={{fontSize: '16px', margin: 0}}>
                      By submitting this form, you agree to our{" "}
                      <a href="/legal/terms-of-use.html" target="_blank" rel="noopener noreferrer" style={{color: '#2B4B8C'}}>
                        Terms of Use
                      </a>
                      {" "}and{" "}
                      <a href="/legal/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{color: '#2B4B8C'}}>
                        Privacy Policy
                      </a>
                      .
                    </p>
                  </div>

                  <div className="text-center">
                    <button
                      type="submit"
                      className="btn btn-custom btn-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending Request...' : 'Get Free Assessment'}
                    </button>
                  </div>
                </form>
            </div>
          </div>
        </div>
      </section>

    </div>
    
    {/* Universal Footer */}
    <Footer />
    </div>
  );
};

export default ProcessPage;